import { Button } from 'antd';
import clsx from 'clsx';
import { FieldValues } from 'react-hook-form';
import Form from '../Form';
import InputField from '../InputField';
import PasswordStrengthIndicator from './PasswordStrengthIndicator';
import { FieldLabels, FieldNames, passwordRequirementsPopover, passwordValidation } from './constants';
import { InputFieldType } from '../../../enums/inputFieldTypes';
import { InputStyle } from '../../../enums/inputEnums';
import { ItemSize } from '../../../enums/sizeEnums';
import { ButtonHtmlType } from '../../../enums/buttomEnums';
import { FormModes } from '../../../enums/formEnums';
import styles from './passwordForm.module.scss';

interface PasswordFormProps {
  onSubmit: (newPassword: string) => void;
  onCancel: () => void;
  submitLabel?: string;
  cancelLabel?: string;
  passwordLabel?: string;
  confirmLabel?: string;
  passwordPlaceholder?: string;
  confirmPlaceholder?: string;
  isSubmitting?: boolean;
  isCancelling?: boolean;
  cancelIcon?: React.ReactNode;
  submitIcon?: React.ReactNode;
  /** Content-hugging button sizing for compact contexts (e.g. a modal) instead of the full-width Auth screen style. */
  compactButtons?: boolean;
}

const PasswordForm: React.FC<PasswordFormProps> = ({
  onSubmit,
  onCancel,
  submitLabel = 'SUBMIT',
  cancelLabel = 'CANCEL',
  passwordLabel = FieldLabels.PASSWORD,
  confirmLabel = FieldLabels.CONFIRM_PASSWORD,
  passwordPlaceholder,
  confirmPlaceholder,
  isSubmitting = false,
  isCancelling = false,
  cancelIcon,
  submitIcon,
  compactButtons = false,
}) => {
  const { passwordField, confirmPasswordField } = FieldNames;

  const handleSubmitForm = (values: FieldValues) => {
    onSubmit(values.confirmPassword);
  };

  return (
    <div className={styles.fieldsContainer}>
      <Form validationSchema={passwordValidation} onSubmit={handleSubmitForm} mode={FormModes.OnSubmit}>
        {() => (
          <>
            <InputField
              type={InputFieldType.PASSWORD}
              name={passwordField}
              label={passwordLabel}
              placeholder={passwordPlaceholder}
              inputStyle={InputStyle.LOGIN}
              size={ItemSize.LARGE}
              visibilityToggle={true}
              className={styles.passwordInput}
              infoPopover={passwordRequirementsPopover}
            />
            <PasswordStrengthIndicator />
            <InputField
              type={InputFieldType.PASSWORD}
              name={confirmPasswordField}
              label={confirmLabel}
              placeholder={confirmPlaceholder}
              inputStyle={InputStyle.LOGIN}
              size={ItemSize.LARGE}
              visibilityToggle={true}
              className={styles.passwordInput}
              infoPopover={passwordRequirementsPopover}
            />
            <div className={styles.buttonRow}>
              <Button
                className={clsx(styles.actionButton, {
                  [styles.actionButtonCompact]: compactButtons,
                  [styles.actionButtonCompactLeading]: compactButtons,
                })}
                size={ItemSize.LARGE}
                loading={isCancelling}
                disabled={isSubmitting}
                onClick={onCancel}
                icon={cancelIcon}
              >
                {cancelLabel}
              </Button>
              <Button
                className={clsx(styles.actionButton, {
                  [styles.actionButtonCompact]: compactButtons,
                  [styles.actionButtonCompactTrailing]: compactButtons,
                })}
                size={ItemSize.LARGE}
                htmlType={ButtonHtmlType.Submit}
                loading={isSubmitting}
                disabled={isCancelling}
                icon={submitIcon}
                iconPosition="end"
              >
                {submitLabel}
              </Button>
            </div>
          </>
        )}
      </Form>
    </div>
  );
};

export default PasswordForm;
